import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

type CatActivo = {
  id: string;
  clave: string;
  nombre: string;
  tipo_bien: 'mueble' | 'inmueble' | 'intangible';
  vida_util_meses: number;
  tasa_anual: number;
};
type Proveedor = { id: string; rfc: string; razon_social: string };
type PresupEg = { id: string; partida_codigo: string; partida_nombre: string; disponible: number };

export function BienFormModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [v, setV] = useState({
    clave: '', descripcion: '', tipo: 'mueble' as 'mueble'|'inmueble'|'intangible'|'vehiculo',
    categoria_activo_id: '', marca: '', modelo: '', numero_serie: '', color: '',
    condiciones: 'nuevo', caracteristicas: '',
    fecha_adquisicion: new Date().toISOString().slice(0, 10),
    valor_adquisicion: 0, valor_residual: 0,
    proveedor_id: '', unidad_administrativa_id: '', ubicacion: '',
    garantia_hasta: '',
    emitir_poliza: true, afectar_presupuesto: true, presupuesto_egreso_id: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: categorias } = useQuery({
    queryKey: ['categorias-activo'],
    queryFn: () => api<CatActivo[]>('/categorias-activo'),
    enabled: open,
  });
  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-pick'],
    queryFn: () => api<{ items: Proveedor[] }>('/proveedores?limit=500&solo_activos=true'),
    enabled: open,
  });
  const { data: presupuestos } = useQuery({
    queryKey: ['presupuestos-egresos'],
    queryFn: () => api<PresupEg[]>('/presupuesto/egresos'),
    enabled: open,
  });

  const categoriasFiltradas = (categorias ?? []).filter(c =>
    v.tipo === 'vehiculo' ? c.clave === 'VEH' : c.tipo_bien === v.tipo
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api('/bienes', {
        method: 'POST',
        body: {
          ...v,
          tipo: v.tipo === 'vehiculo' ? 'mueble' : v.tipo,  // internamente vehiculo es mueble
          garantia_hasta: v.garantia_hasta || undefined,
        },
      });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally {
      setSubm(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Alta patrimonial"
      subtitle="Genera póliza de activación (1.2.3/4 D · 2.1.2/4.1.1 H), afecta presupuesto ejercido y lo deja listo para asignar resguardo."
      size="xl"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="bien-form" loading={subm}>Dar de alta</Button>
        </>
      }
    >
      <form id="bien-form" onSubmit={submit} className="flex flex-col gap-5">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">{err}</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Input name="clave" label="Clave patrimonial" required
                 value={v.clave} onChange={(e) => setV({ ...v, clave: e.target.value.toUpperCase() })} />
          <div>
            <label className="text-[13px] font-medium text-ink">Tipo</label>
            <select value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value as any, categoria_activo_id: '' })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="mueble">Mueble</option>
              <option value="inmueble">Inmueble</option>
              <option value="vehiculo">Vehículo</option>
              <option value="intangible">Intangible</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[13px] font-medium text-ink">Categoría de activo fijo</label>
            <select value={v.categoria_activo_id} onChange={(e) => setV({ ...v, categoria_activo_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(seleccione)</option>
              {categoriasFiltradas.map(c => (
                <option key={c.id} value={c.id}>{c.clave} — {c.nombre} (vida {c.vida_util_meses}m, {c.tasa_anual}%)</option>
              ))}
            </select>
          </div>

          <Input name="descripcion" label="Descripción" required className="sm:col-span-4"
                 value={v.descripcion} onChange={(e) => setV({ ...v, descripcion: e.target.value })} />

          {(v.tipo === 'mueble' || v.tipo === 'vehiculo') && (
            <>
              <Input name="marca" label="Marca" value={v.marca} onChange={(e) => setV({ ...v, marca: e.target.value })} />
              <Input name="modelo" label="Modelo" value={v.modelo} onChange={(e) => setV({ ...v, modelo: e.target.value })} />
              <Input name="numero_serie" label="Número de serie" value={v.numero_serie} onChange={(e) => setV({ ...v, numero_serie: e.target.value })} />
              <Input name="color" label="Color" value={v.color} onChange={(e) => setV({ ...v, color: e.target.value })} />
              <div>
                <label className="text-[13px] font-medium text-ink">Condiciones</label>
                <select value={v.condiciones} onChange={(e) => setV({ ...v, condiciones: e.target.value })}
                  className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
                  <option value="nuevo">Nuevo</option>
                  <option value="usado">Usado</option>
                  <option value="rehabilitado">Rehabilitado</option>
                  <option value="obsoleto">Obsoleto</option>
                </select>
              </div>
              <Input name="garantia_hasta" label="Garantía hasta" type="date"
                     value={v.garantia_hasta} onChange={(e) => setV({ ...v, garantia_hasta: e.target.value })} />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Input name="fecha_adquisicion" label="Fecha adquisición" type="date" required
                 value={v.fecha_adquisicion} onChange={(e) => setV({ ...v, fecha_adquisicion: e.target.value })} />
          <Input name="valor_adquisicion" label="Valor de adquisición" type="number" step="0.01" required
                 value={v.valor_adquisicion} onChange={(e) => setV({ ...v, valor_adquisicion: parseFloat(e.target.value) || 0 })} />
          <Input name="valor_residual" label="Valor residual (desecho)" type="number" step="0.01" hint="Para depreciación"
                 value={v.valor_residual} onChange={(e) => setV({ ...v, valor_residual: parseFloat(e.target.value) || 0 })} />
          <Input name="ubicacion" label="Ubicación física"
                 value={v.ubicacion} onChange={(e) => setV({ ...v, ubicacion: e.target.value })} />

          <div className="sm:col-span-2">
            <label className="text-[13px] font-medium text-ink">Proveedor</label>
            <select value={v.proveedor_id} onChange={(e) => setV({ ...v, proveedor_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(sin proveedor / donación)</option>
              {(proveedores?.items ?? []).map(p => (
                <option key={p.id} value={p.id}>{p.rfc} — {p.razon_social}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="text-[13px] font-medium text-ink">Presupuesto a afectar</label>
            <select value={v.presupuesto_egreso_id} onChange={(e) => setV({ ...v, presupuesto_egreso_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(sin afectar)</option>
              {(presupuestos ?? []).map(p => (
                <option key={p.id} value={p.id}>{p.partida_codigo} — {p.partida_nombre}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-[13px] font-medium text-ink">Características</label>
          <textarea value={v.caracteristicas} onChange={(e) => setV({ ...v, caracteristicas: e.target.value })}
            rows={2} className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle" />
        </div>

        <div className="flex flex-wrap items-center gap-6 text-[13px]">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={v.emitir_poliza}
                   onChange={(e) => setV({ ...v, emitir_poliza: e.target.checked })}
                   className="h-3.5 w-3.5 accent-brand-500" />
            Emitir póliza de activación
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={v.afectar_presupuesto}
                   onChange={(e) => setV({ ...v, afectar_presupuesto: e.target.checked })}
                   className="h-3.5 w-3.5 accent-brand-500" />
            Afectar presupuesto (ejercido)
          </label>
        </div>

        {v.tipo === 'inmueble' && (
          <p className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-200">
            Tras dar de alta este inmueble, entra al detalle para capturar datos catastrales, coordenadas GPS y escritura.
          </p>
        )}
      </form>
    </Modal>
  );
}
